/*
  clikt. — product cards that behave like a gallery.

  Hover does two things: the photograph creeps in slightly, and arrows plus
  an eye fade in over it. The arrows step through the product's images in
  place; the eye opens the buy box without leaving the grid.

  Everything is delegated from the document, so cards that arrive later —
  pagination, a filter change, the theme editor re-rendering a section —
  behave the same without anything having to re-bind.
*/
(function () {
  /* ------------------------------------------------------------ gallery */

  function slidesOf(card) {
    return [card.querySelector('.media > img:not(.clikt-card__slide)')]
      .concat([...card.querySelectorAll('.clikt-card__slide')])
      .filter(Boolean);
  }

  /*
    Mark the first photograph active on every card up front. Without it the
    "not active" rule would fade all of them out and the grid would render
    empty until something was clicked.
  */
  function prime() {
    document.querySelectorAll('.card__inner').forEach(function (card) {
      const first = card.querySelector('.media > img:not(.clikt-card__slide)');
      if (first && !first.classList.contains('is-active')) first.classList.add('is-active');

      // Release the extras now rather than on the first click. An element
      // revealed in the same frame its opacity changes has no previous value
      // to animate from, so the incoming photograph would snap in while the
      // outgoing one faded — half a cross-fade.
      card.querySelectorAll('.clikt-card__slide[hidden]').forEach(function (s) {
        s.hidden = false;
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', prime);
  } else {
    prime();
  }

  function show(card, index) {
    const slides = slidesOf(card);
    if (slides.length < 2) return;

    /*
      Cross-fade, so the images have to stay in the layout — the hidden
      attribute is a hard cut. They start hidden in the markup so nothing
      flashes before this runs; the first pass releases them and from then
      on only the active class moves.
    */
    // Wrap at both ends: someone flicking back from the first image expects
    // the last one, not a dead button.
    const next = (index + slides.length) % slides.length;
    slides.forEach((img, i) => {
      img.classList.toggle('is-active', i === next);
    });
    card.dataset.cliktIndex = String(next);
  }

  document.addEventListener('click', function (e) {
    const arrow = e.target.closest('[data-clikt-prev], [data-clikt-next]');
    if (!arrow) return;

    // The whole card is clickable; stepping through images must not follow it.
    e.preventDefault();
    e.stopPropagation();

    const card = arrow.closest('.card__inner');
    if (!card) return;
    const current = Number(card.dataset.cliktIndex || 0);
    show(card, current + (arrow.hasAttribute('data-clikt-next') ? 1 : -1));
  });

  /*
    Click anywhere on the card to open the product.

    This replaces the invisible overlay Dawn stretches across the card, which
    could not be reliably layered beneath the arrows and the eye. Doing it
    here means the question "was this a control?" is asked in plain code
    rather than settled by stacking rules — and real links inside the card
    (the title, the vendor) still behave as themselves.
  */
  document.addEventListener('click', function (e) {
    const wrapper = e.target.closest('.card-wrapper');
    if (!wrapper) return;
    if (e.target.closest('a, button, [data-clikt-quickview], [data-clikt-prev], [data-clikt-next]')) return;
    if (e.metaKey || e.ctrlKey || e.button !== 0) return;

    const link = wrapper.querySelector('.card__heading a[href]');
    if (link) window.location = link.href;
  });

  /*
    Leaving the card puts the first photograph back. The grid should look the
    same on the way back down the page as it did on the way up.

    mouseleave does not bubble, so this listens in capture and reacts ONLY
    when the element being left is the card wrapper itself. Reacting to any
    descendant was the bug that made the arrows look dead: hiding the image
    under the cursor fired mouseleave on that image, which reset the card to
    photo one in the same instant the arrow advanced it.
  */
  document.addEventListener(
    'mouseleave',
    function (e) {
      if (!e.target.classList || !e.target.classList.contains('card-wrapper')) return;
      const card = e.target.querySelector('.card__inner');
      if (card && card.dataset.cliktIndex && card.dataset.cliktIndex !== '0') show(card, 0);
    },
    true
  );

  /* ---------------------------------------------------------- quick view */

  let modal = null;

  function ensureModal() {
    if (modal) return modal;
    modal = document.createElement('div');
    modal.className = 'clikt-quickview';
    modal.innerHTML =
      '<div class="clikt-quickview__backdrop" data-qv-close></div>' +
      '<div class="clikt-quickview__panel" role="dialog" aria-modal="true" aria-label="Quick view">' +
      '<button type="button" class="clikt-quickview__close" data-qv-close aria-label="Close">&times;</button>' +
      '<div class="clikt-quickview__body"></div>' +
      '</div>';
    document.body.appendChild(modal);

    modal.addEventListener('click', function (e) {
      if (e.target.closest('[data-qv-close]')) close();
    });
    return modal;
  }

  function close() {
    if (!modal) return;
    modal.classList.remove('is-open');
    document.documentElement.classList.remove('clikt-qv-open');
  }

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') close();
  });

  document.addEventListener('click', async function (e) {
    const trigger = e.target.closest('[data-clikt-quickview]');
    if (!trigger) return;
    e.preventDefault();
    e.stopPropagation();

    const url = trigger.dataset.cliktQuickview;
    const el = ensureModal();
    const body = el.querySelector('.clikt-quickview__body');

    body.innerHTML = '<p class="clikt-quickview__loading">Loading…</p>';
    el.classList.add('is-open');
    document.documentElement.classList.add('clikt-qv-open');

    try {
      // An alternate template rather than the whole product page: it returns
      // the buy box on its own, so nothing has to be scraped out of a full
      // document and the markup stays in Liquid where it belongs.
      const res = await fetch(url + (url.indexOf('?') > -1 ? '&' : '?') + 'view=quickview');
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.querySelector('.clikt-qv');
      body.innerHTML = content ? content.outerHTML : '';
      if (!content) window.location = url;
    } catch (err) {
      // A failed peek should still get them to the product.
      window.location = url;
    }
  });
})();
